/* Native Shopify localization recommendations. Every market change goes through the native form:
   visitors submit it themselves, except for forced countries (data-forced), which it submits for them. */
if (!customElements.get('heimat-market-selector')) {
  customElements.define('heimat-market-selector', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.form = this.querySelector('form');
      this.countries = [...this.querySelectorAll('[name="country_code"]')];
      this.countryPicker = this.querySelector('[data-country-picker]');
      this.countryToggle = this.countryPicker.querySelector('summary');
      this.languagePicker = this.querySelector('[data-language-picker]');
      this.languageOptions = this.querySelector('[data-language-options]');
      this.pickers = [this.countryPicker, this.languagePicker];
      this.recommendation = this.querySelector('[data-recommendation]');
      this.copy = JSON.parse(this.querySelector('[data-copy-config]').textContent);
      this.originalCopy = new Map([...this.querySelectorAll('[data-copy]')].map(el => [el, el.textContent]));
      this.originalClose = this.querySelector('.heimat-market__close').ariaLabel;
      this.storageKey = 'heimat-market-preference-v2';
      this.detectedKey = 'heimat-market-detected';
      this.forcedKey = 'heimat-market-forced';
      this.designMode = Boolean(window.Shopify?.designMode);
      this.interacted = false;

      this.revealTriggers();
      document.addEventListener('shopify:section:load', () => this.revealTriggers(), options);
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-heimat-market-open]');
        if (!trigger) return;
        this.interacted = true;
        this.resetSelection();
        const drawer = trigger.closest('[data-heimat-drawer]');
        if (drawer) drawer.querySelector('[data-heimat-drawer-close]')?.click();
        this.open(drawer ? document.querySelector('[data-heimat-burger]') : trigger);
      }, options);
      this.countryPicker.addEventListener('change', () => {
        this.updateCountry(this.languageValue);
        this.recommendation.hidden = true;
      }, options);
      this.languagePicker.addEventListener('change', () => this.updateLanguageLabel(), options);
      this.pickers.forEach(picker => {
        const list = picker.querySelector('fieldset');
        if (typeof list.showPopover === 'function') list.setAttribute('popover', 'manual');
        picker.querySelector('summary').addEventListener('click', event => {
          if (event.currentTarget.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            return;
          }
          if (!picker.open) {
            this.pickers.forEach(other => { if (other !== picker) other.open = false; });
            this.positionPicker(picker);
          }
        }, options);
        picker.addEventListener('toggle', () => {
          if (!picker.open) {
            if (list.hasAttribute('popover') && list.matches(':popover-open')) list.hidePopover();
            return;
          }
          if (list.hasAttribute('popover')) list.showPopover();
          this.positionPicker(picker);
          const selected = list.querySelector('input:checked')?.closest('label');
          if (selected) {
            const row = selected.getBoundingClientRect();
            list.scrollTop += row.top - list.getBoundingClientRect().top - (list.clientHeight - row.height) / 2;
          }
        }, options);
        picker.addEventListener('click', event => {
          // A tap usually lands on the option's flag or label text rather than the radio,
          // and the label then forwards a synthetic click (detail 0) to the input, so
          // matching the input alone left the list open. Match the whole option row on the
          // pointer event instead; keyboard activation still goes through keydown below.
          if (event.target.closest('.heimat-market__picker-option') && event.detail > 0) {
            // Finish native radio activation before moving focus and hiding the list.
            requestAnimationFrame(() => { if (this.isConnected) this.closePicker(picker); });
          }
        }, options);
        picker.addEventListener('keydown', event => {
          if (!picker.open) return;
          if (event.key === 'Tab') {
            requestAnimationFrame(() => {
              if (this.isConnected && !picker.contains(document.activeElement)) picker.open = false;
            });
          }
          if (event.key === 'Escape' || (event.key === 'Enter' && event.target.matches('input[type="radio"]'))) {
            event.preventDefault();
            event.stopPropagation();
            this.closePicker(picker);
          }
        }, options);
      });
      document.addEventListener('click', event => {
        this.pickers.forEach(picker => {
          if (picker.open && !picker.contains(event.target)) picker.open = false;
        });
      }, options);
      const reposition = () => this.pickers.forEach(picker => { if (picker.open) this.positionPicker(picker); });
      this.dialog.addEventListener('scroll', reposition, options);
      window.addEventListener('resize', reposition, options);
      window.visualViewport?.addEventListener('resize', reposition, options);
      window.visualViewport?.addEventListener('scroll', reposition, options);
      this.querySelectorAll('[data-close]').forEach(button => button.addEventListener('click', () => this.dismiss(), options));
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.dismiss();
      }, options);
      this.dialog.addEventListener('click', event => {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) this.dismiss();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.pickers.forEach(picker => { picker.open = false; });
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, options);
      document.addEventListener('submit', event => {
        // Also respect deliberate choices made through the existing footer forms.
        if (event.target.querySelector('[name="form_type"][value="localization"]')) this.remember();
      }, options);
      this.form.addEventListener('submit', event => {
        if (this.designMode) {
          event.preventDefault();
          this.dialog.close();
          return;
        }
        this.remember();
        // Carry the explicit choice across market domains via Shopify's native return_to.
        const returnTo = this.form.querySelector('[name="return_to"]');
        if (returnTo) {
          const url = new URL(returnTo.value || window.location.pathname, window.location.origin);
          url.searchParams.set('heimat_market_selected', '1');
          returnTo.value = url.pathname + url.search + window.location.hash;
        }
      }, options);
      this.updateCountry(this.dataset.language);

      if (this.designMode) {
        if (this.dataset.preview === 'true') this.open();
        return;
      }
      const url = new URL(window.location.href);
      if (url.searchParams.get('heimat_market_selected') === '1') {
        this.remember();
        url.searchParams.delete('heimat_market_selected');
        window.history.replaceState(window.history.state, '', url);
      }
      // Forced countries are where a visitor located there starts, without a prompt. A country
      // the visitor picked or kept themselves wins for good, not only for the remember period
      // (HG-166). Only countries Shopify offers count.
      this.forced = this.hasDecision(true) ? [] : (this.dataset.forced || '').split(',')
        .filter(code => code && code !== this.dataset.country && this.countries.some(country => country.value === code));
      const detected = this.session(this.detectedKey);
      if (this.forced.includes(detected) && this.force(detected)) return;
      const suggest = this.dataset.auto !== 'false' && !this.hasDecision();
      if (suggest || (this.forced.length && !detected)) this.detect(suggest);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
      this.dialog?.close();
    }

    revealTriggers() {
      document.querySelectorAll('[data-heimat-market-open]').forEach(button => { button.hidden = false; });
    }

    // A stored choice counts for the remember period. With anyAge it counts however old it is.
    hasDecision(anyAge) {
      if (this.interacted) return true;
      try {
        const timestamp = Number(localStorage.getItem(this.storageKey));
        const age = Date.now() - timestamp;
        return timestamp > 0 && age >= 0 && (anyAge || age < Number(this.dataset.rememberDays) * 86400000);
      } catch (_) {
        return false;
      }
    }

    remember() {
      this.interacted = true;
      if (this.designMode) return;
      try { localStorage.setItem(this.storageKey, String(Date.now())); } catch (_) { /* Storage may be unavailable. */ }
    }

    // Tab-session storage: read with one argument, write with two, remove with null.
    session(key, value) {
      try {
        if (value === undefined) return sessionStorage.getItem(key);
        if (value === null) sessionStorage.removeItem(key);
        else sessionStorage.setItem(key, value);
      } catch (_) { /* Storage may be unavailable. */ }
      return null;
    }

    // Moves the visitor to a forced country without asking. The marker allows one attempt
    // per tab session, so a failed switch cannot loop.
    force(code, language) {
      if (this.session(this.forcedKey)) return false;
      this.session(this.forcedKey, code);
      if (this.session(this.forcedKey) !== code || !this.selectCountry(code)) return false;
      this.updateCountry(language || this.dataset.language);
      this.form.submit();
      return true;
    }

    selectCountry(code) {
      const country = this.countries.find(country => country.value === code);
      if (!country) return false;
      country.checked = true;
      return true;
    }

    closePicker(picker) {
      picker.querySelector('summary').focus({ preventScroll: true });
      picker.open = false;
    }

    positionPicker(picker) {
      const trigger = picker.querySelector('summary').getBoundingClientRect();
      const list = picker.querySelector('fieldset');
      const viewport = window.visualViewport;
      // Top-layer lists escape the dialog's scroll container, while remaining in the native form.
      const bounds = list.hasAttribute('popover')
        ? { top: viewport?.offsetTop || 0, bottom: (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) }
        : this.dialog.getBoundingClientRect();
      const below = bounds.bottom - trigger.bottom - 18;
      const above = trigger.top - bounds.top - 18;
      const desiredHeight = Math.min(240, list.scrollHeight + 2);
      const upwards = below < desiredHeight && above > below;
      picker.dataset.direction = upwards ? 'up' : 'down';
      picker.style.setProperty('--hm-list-height', `${Math.max(0, Math.min(240, upwards ? above : below))}px`);
      if (list.hasAttribute('popover')) {
        list.style.left = `${trigger.left}px`;
        list.style.width = `${trigger.width}px`;
        list.style.top = `${upwards ? trigger.top - 6 - list.getBoundingClientRect().height : trigger.bottom + 6}px`;
      }
    }

    get languageValue() {
      return this.languageOptions.querySelector('input:checked')?.value || '';
    }

    updateLanguageLabel() {
      const selected = this.languageOptions.querySelector('input:checked');
      this.querySelector('[data-selected-language]').textContent = selected?.nextElementSibling.textContent || '—';
    }

    updateCountry(preferredLanguage) {
      const selected = this.countries.find(country => country.checked);
      if (!selected) return;
      this.querySelector('[data-selected-country]').textContent = `${selected.dataset.countryName} (${selected.dataset.currencySymbol})`;
      this.querySelector('[data-selected-flag]').src = selected.dataset.flag;
      const languages = JSON.parse(selected.dataset.languages);
      // An empty list means Shopify has not exposed languages for this market.
      // Omit locale_code and let Shopify resolve the destination's default language.
      const selectedLanguage = languages.find(language => language.code === preferredLanguage)
        || languages.find(language => language.primary) || languages[0];
      this.languageOptions.replaceChildren(...languages.map(language => {
        const label = document.createElement('label');
        label.className = 'heimat-market__picker-option';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'locale_code';
        input.value = language.code;
        input.checked = language === selectedLanguage;
        const text = document.createElement('span');
        text.lang = language.code;
        text.textContent = language.name;
        label.append(input, text);
        return label;
      }));
      this.languagePicker.querySelector('summary').setAttribute('aria-disabled', String(!languages.length));
      this.updateLanguageLabel();
    }

    resetSelection() {
      this.selectCountry(this.dataset.country);
      this.pickers.forEach(picker => { picker.open = false; });
      this.updateCountry(this.dataset.language);
      this.originalCopy.forEach((text, element) => { element.textContent = text; });
      this.dialog.lang = this.dataset.language;
      this.querySelector('.heimat-market__close').ariaLabel = this.originalClose;
      this.recommendation.hidden = true;
    }

    open(opener) {
      if (this.dialog.open || !this.isConnected) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      this.countryToggle.focus({ preventScroll: true });
    }

    dismiss() {
      this.remember();
      this.dialog.close();
    }

    async detect(suggest = true) {
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 4000);
      try {
        const url = new URL(`${this.dataset.root.replace(/\/?$/, '/')}browsing_context_suggestions.json`, window.location.origin);
        url.searchParams.set('country[enabled]', 'true');
        url.searchParams.set('country[exclude]', this.dataset.country);
        url.searchParams.set('language[enabled]', 'true');
        url.searchParams.set('language[exclude]', this.dataset.language);
        const response = await fetch(url, { credentials: 'same-origin', signal: this.request.signal });
        if (!response.ok) return;
        const result = await response.json();
        if (!this.isConnected) return;
        const suggestion = result.suggestions?.[0]?.parts;
        const detected = result.detected_values?.country?.handle;
        if (detected) this.session(this.detectedKey, detected);
        if (this.forced.includes(detected) && this.force(detected, suggestion?.language?.handle)) return;
        if (!suggest || this.hasDecision()) return;
        if (!suggestion) return;
        // Only interrupt for a different market. A language-only suggestion (e.g. an
        // English-language phone in Germany) preselected English on the German store,
        // and one tap on the primary button moved the visitor to /en (FAT-511).
        const code = suggestion.country?.handle;
        if (!code || code === this.dataset.country || !this.selectCountry(code)) return;
        this.updateCountry(suggestion.language?.handle || this.dataset.language);

        const copy = this.copy[this.languageValue];
        if (copy) {
          this.querySelectorAll('[data-copy]').forEach(element => { element.textContent = copy[element.dataset.copy]; });
          this.dialog.lang = this.languageValue;
          this.querySelector('.heimat-market__close').ariaLabel = copy.close;
        }
        // Only claim a location recommendation when Shopify's detected country agrees.
        if (code === detected) {
          let countryName = this.countries.find(country => country.checked).dataset.countryName;
          try { countryName = new Intl.DisplayNames([this.dialog.lang], { type: 'region' }).of(code); } catch (_) { /* Use Shopify's label. */ }
          this.recommendation.textContent = (copy?.recommendation || this.copy.currentRecommendation).replace('{country}', countryName);
          this.recommendation.hidden = false;
        }
        this.scheduleOpen();
      } catch (_) {
        // Keep manual selection available when Shopify's suggestion endpoint fails.
      } finally {
        clearTimeout(timeout);
      }
    }

    scheduleOpen(attempt = 0) {
      this.timer = setTimeout(() => {
        if (!this.isConnected || this.hasDecision()) return;
        // Let an active dialog, cookie choice, menu or text entry finish first.
        const busy = document.hidden || [...document.querySelectorAll('dialog[open], [aria-modal="true"], .heimat-drawer[data-open="true"], .heimat-search[data-open="true"], [data-heimat-inline-search][data-open="true"], cart-drawer.active')]
          .some(element => element !== this.dialog && element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true }))
          || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
        if (busy) {
          if (attempt < 20) this.scheduleOpen(attempt + 1);
          return;
        }
        this.open();
      }, attempt === 0 ? Number(this.dataset.delay) * 1000 : 1000);
    }
  });
}
